import { Flex, useMantineTheme } from '@mantine/core';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { Pill, Skeleton, Text } from '@feathr/components';
import { useStore } from '@feathr/extender/state';

interface IGeoFiltersListProps {
  includedGeoFilterIds: string[];
  excludedGeoFilterIds: string[];
}

function GeoFiltersList({
  includedGeoFilterIds,
  excludedGeoFilterIds,
}: Readonly<IGeoFiltersListProps>): JSX.Element {
  const { t } = useTranslation();
  const { GeoFilters } = useStore();
  const theme = useMantineTheme();

  const allGeoFilterIds = [...includedGeoFilterIds, ...excludedGeoFilterIds];
  const geoFiltersList = allGeoFilterIds.length
    ? GeoFilters.list({
        filters: { id__in: allGeoFilterIds },
        pagination: { page: 0, page_size: 50 },
      })
    : undefined;
  const geoFilters = (geoFiltersList?.models ?? []).map((geoFilter) => ({
    ...geoFilter.toJS(),
    included: includedGeoFilterIds.includes(geoFilter.id),
  }));

  if (geoFiltersList?.isPending) {
    return <Skeleton paragraphs={3} title={true} width={'100%'} />;
  }

  return (
    <>
      {geoFilters.length > 0 && (
        <Flex direction={'column'} gap={theme.spacing[2]}>
          {geoFilters.map((geoFilter) => (
            <Flex align={'center'} justify={'space-between'} key={geoFilter.id}>
              <Flex align={'center'} gap={theme.spacing[2]}>
                <Text.DefaultSemibold>{geoFilter.name}</Text.DefaultSemibold>
                <Text.DefaultRegular>
                  {geoFilter.address.region && `${geoFilter.address.region}, `}
                  {geoFilter.address.country}
                </Text.DefaultRegular>
              </Flex>
              <Flex align={'center'} gap={theme.spacing[2]}>
                <Text.SmallRegular>{geoFilter.kind}</Text.SmallRegular>
                <Pill theme={geoFilter.included ? 'green' : 'red'}>
                  {geoFilter.included ? t('Included') : t('Excluded')}
                </Pill>
              </Flex>
            </Flex>
          ))}
        </Flex>
      )}
    </>
  );
}

export default observer(GeoFiltersList);
